import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ToastProvider } from './contexts/ToastContext';
import { setupAuthDebugger } from './utils/debugAuth';

// Layouts
import { MainLayout } from './components/layouts/MainLayout';
import { AuthLayout } from './components/layouts/AuthLayout';

// Pages
import { HomePage } from './pages/HomePage';
import { LoginPage } from './pages/auth/LoginPage';
import { RegisterPage } from './pages/auth/RegisterPage';
import { ForumPage } from './pages/forum/ForumPage';
import { DiscuzTopicPage } from './pages/forum/DiscuzTopicPage';
import { ThreadPage } from './pages/forum/ThreadPage';
import { ProfilePage } from './pages/profile/ProfilePage';
import { MessagingPage } from './pages/messaging/MessagingPage';
import { MarketplaceBrowsePage } from './pages/marketplace/MarketplaceBrowsePage';
import { MarketplaceDetailPage } from './pages/marketplace/MarketplaceDetailPage';
import { MarketplaceSellerPage } from './pages/marketplace/MarketplaceSellerPage';
import MarketplaceBuyerPage from './pages/marketplace/MarketplaceBuyerPage';
import BuyerProfilePage from './pages/marketplace/BuyerProfilePage';
import { MarketplaceCreatePage } from './pages/marketplace/MarketplaceCreatePage';
import { MarketplaceEditPage } from './pages/marketplace/MarketplaceEditPage';
import { MarketplaceAdminPage } from './pages/marketplace/MarketplaceAdminPage';
import { MarketplaceAdminDetailPage } from './pages/marketplace/MarketplaceAdminDetailPage';
import { RankingPage } from './pages/ranking/RankingPage';
import { AdsPage } from './pages/ads/AdsPage';
import { ModerationPage } from './pages/admin/ModerationPage';
import { CategoryManagement } from './features/topics/CategoryManagement';
import { TopicManagement } from './features/topics/TopicManagement';
import { AboutPage } from './pages/AboutPage';
import { PrivacyPage } from './pages/PrivacyPage';
import { TermsPage } from './pages/TermsPage';
import { ContactPage } from './pages/ContactPage';
import { AppealsPage } from './pages/appeals/AppealsPage';
import { AuthExpiredHandler } from './components/auth/AuthExpiredHandler';

// Protected Route Component
import { ProtectedRoute } from './components/auth/ProtectedRoute';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  useEffect(() => {
    // Setup debug utilities in console
    setupAuthDebugger();
    console.log('💡 Debug utilities loaded! Type debugAuth() in console for auth info');
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <ToastProvider>
          <AuthProvider>
            <BrowserRouter>
            <AuthExpiredHandler />
            <Routes>
            {/* Public routes */}
            <Route element={<MainLayout />}>
              <Route path="/" element={<HomePage />} />

              {/* Auth routes */}
              <Route element={<AuthLayout />}>
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
              </Route>

              {/* Forum routes (public read, auth for write) */}
              <Route path="/forum" element={<ForumPage />} />
              <Route path="/forum/topics/:slug" element={<DiscuzTopicPage />} />
              <Route path="/forum/thread/:threadId" element={<ThreadPage />} />

              {/* Rankings (public) */}
              <Route path="/ranking" element={<RankingPage />} />

              {/* Ads (public) */}
              <Route path="/ads" element={<AdsPage />} />

              {/* Footer pages (public) */}
              <Route path="/about" element={<AboutPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/contact" element={<ContactPage />} />

              {/* Marketplace routes */}
              {/* Public marketplace routes */}
              <Route path="/marketplace" element={<MarketplaceBrowsePage />} />
              <Route path="/marketplace/listing/:listingId" element={<MarketplaceDetailPage />} />
              <Route path="/marketplace/buyer-profile/:buyerAlias" element={<BuyerProfilePage />} />

              {/* Protected routes - require authentication */}
              <Route element={<ProtectedRoute />}>
                <Route path="/profile" element={<ProfilePage />} />
                <Route path="/appeals" element={<AppealsPage />} />
                <Route path="/messages" element={<MessagingPage />} />
                <Route path="/marketplace/seller" element={<MarketplaceSellerPage />} />
                <Route path="/marketplace/buyer" element={<MarketplaceBuyerPage />} />
                <Route path="/marketplace/create" element={<MarketplaceCreatePage />} />
                <Route path="/marketplace/edit/:listingId" element={<MarketplaceEditPage />} />
              </Route>

              {/* Admin/Moderator routes - require moderator or admin role */}
              <Route element={<ProtectedRoute requiredRoles={['moderator', 'admin']} />}>
                <Route path="/admin/moderation" element={<ModerationPage />} />
                <Route path="/admin/categories" element={<CategoryManagement />} />
                <Route path="/admin/topics" element={<TopicManagement />} />
                <Route path="/marketplace/admin" element={<MarketplaceAdminPage />} />
                <Route path="/marketplace/admin/listing/:listingId" element={<MarketplaceAdminDetailPage />} />
              </Route>

              {/* Catch all */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
            </Routes>
          </BrowserRouter>
        </AuthProvider>
        </ToastProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
